import React, { forwardRef, useEffect, useState } from 'react';
import './timeInput.css';

interface TimeInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    width?: number;
    height?: number;
}

const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(({
    value,
    onChange,
    placeholder,
    disabled = false,
    width,
    height = 30,
}, ref) => {

    const [isTimeValid, setIsTimeValid] = useState<boolean>(true);

    // Validate time format HH:MM:SS
    const validateTimeFormat = (time: string): boolean => {
        if (!time) return true; // Empty is valid
        
        // Accept both HH:MM:SS and HH:MM formats
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
        const match = time.match(timeRegex);
        
        if (!match) return false;
        
        const hours = parseInt(match[1]);
        const minutes = parseInt(match[2]);
        const seconds = match[3] ? parseInt(match[3]) : 0;

        return hours >= 0 && hours <= 23 && 
               minutes >= 0 && minutes <= 59 && 
               seconds >= 0 && seconds <= 59;
    };

    const handleTimeChange = (time: string) => {
        // Remove any non-digit characters from input
        let numbersOnly = time.replace(/\D/g, '');
        
        // Format with colons
        let formattedTime = '';
        if (numbersOnly.length > 0) {
            // Handle hours (first two digits)
            if (numbersOnly.length >= 2) {
                formattedTime = numbersOnly.slice(0, 2);
                // Add colon after hours
                if (numbersOnly.length > 2) {
                    formattedTime += ':';
                    // Handle minutes (next two digits)
                    if (numbersOnly.length >= 4) {
                        formattedTime += numbersOnly.slice(2, 4);
                        // Add colon after minutes
                        if (numbersOnly.length > 4) {
                            formattedTime += ':';
                            // Handle seconds (last two digits)
                            formattedTime += numbersOnly.slice(4, 6);
                        }
                    } else {
                        formattedTime += numbersOnly.slice(2);
                    }
                }
            } else {
                formattedTime = numbersOnly;
            }
        }
    
        // Validate the format
        const isValid = validateTimeFormat(formattedTime);
        setIsTimeValid(isValid);
        onChange(formattedTime);
    };

    return (
        <input
            ref={ref}
            type="text"
            value={value || ''}
            onChange={(e) => handleTimeChange(e.target.value)}
            className={`timeInput-inputField ${!isTimeValid ? 'timeInput-invalid' : ''}`}
            disabled={disabled}
            placeholder={placeholder || "00:00:00"}
            style={{ 
                width: width ? `${width}px` : '100%', 
                height: `${height}px` 
            }}
        />
    );
});

TimeInput.displayName = 'TimeInput';

export default TimeInput;